import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormikProps, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';

import {
  StyledContentContainer,
  StyledTitle,
  SplitScreenLayout,
  StyledVideoContainer,
  StyledVideoScreen,
  StyledSideImage,
} from '@selfserve/react-ui';

import {
  routesPaths,
  getRecaptchaToken,
  subscriptionTypes,
  responseTypes,
  apiStatuses,
} from '@selfserve/react-utils';
import {
  useAuth,
  useStorage,
  storage,
  storageKeys,
} from '@selfserve/react-hooks';
import { withErrorHandling } from '@selfserve/react-hocs';

import { CreateAccountForm } from '../ui/create-account-form';
import {
  PackageDetail,
  useCreateAccountRequest,
  usePackageDetailRequest,
} from '../data';

export interface FormData {
  firstName?: string;
  lastName?: string;
  company?: string;
  countryCode?: string;
  jobTitle?: string;
  companySize?: string;
  password: string;
  timezone?: string;
  passwordErrors: {
    minLengthErr: boolean;
    oneNumberErr: boolean;
    upperCaseErr: boolean;
    lowerCaseErr: boolean;
  };
  confirmPassword: string;
  allowReceivingMarketingData?: boolean;
}

const initialValues: FormData = {
  firstName: '',
  lastName: '',
  company: '',
  countryCode: '',
  jobTitle: '',
  companySize: '',
  password: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  passwordErrors: {
    minLengthErr: false,
    oneNumberErr: false,
    upperCaseErr: false,
    lowerCaseErr: false,
  },
  confirmPassword: '',
  allowReceivingMarketingData: true,
};

const StyledCreateAccountContainer = styled.div(
  ({ theme }: { theme?: any }) => ({
    marginTop: '40px',
    [theme.breakpoints.up('lg')]: {
      width: 'calc(100% - 80px)',
      maxWidth: '650px',
      marginLeft: '80px',
    },
  })
);

const StyledCreateAccountTitle = styled(StyledTitle)`
  display: block;
  margin-bottom: 30px;
`;

interface Props {
  isFullForm: boolean;
  validationSchema: (t: TFunction, isUserExist?: boolean) => object;
}

export const CreateAccount = withErrorHandling<Props>(
  ({ openErrorModal, isFullForm, validationSchema }) => {
    const navigate = useNavigate();
    const { setToken } = useAuth();
    const [existingUser, setExistingUser] = useStorage(
      storage.LOCAL,
      storageKeys.EXISTING_USER
    );
    const [isUserExist] = useState(existingUser);
    const [, setPartnerId] = useStorage(storage.LOCAL, storageKeys.PARTNER_ID);
    const [, setPackageData] = useStorage(storage.SESSION, storageKeys.PACKAGE_DATA);

    const [, setLoginUrl] = useStorage(
      storage.LOCAL,
      storageKeys.LOGIN_URL,
      ''
    );
    const [, setSubscriptionId] = useStorage(
      storage.SESSION,
      storageKeys.SUBSCRIPTION_ID
    );
    const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const { token } = useParams();
    const {
      mutateAsync: handleCreateAccountRequest,
      data: createAccountResponse,
      isLoading: createAccountLoading,
    } = useCreateAccountRequest(initialValues, token as string);
    const { data: { data: packageData = {}, status: packageStatus } = {} } = usePackageDetailRequest(
      token as string
    );

    useEffect(() => {
        if (packageData) {
          setPackageData(packageData);
        }
      }
    , [packageData]);

    const defaultMediaLink = process.env['NX_INFO_VIDEO'];

    const onSubmit = (values: FormData) => {
      setIsRecaptchaLoading(true);
      if (
        !Object.keys(errors).length &&
        !createAccountLoading &&
        !isRecaptchaLoading
      ) {
        getRecaptchaToken().then((recaptchaToken: string) => {
          setIsRecaptchaLoading(false);
          const { passwordErrors, confirmPassword, password, ...data } = values;
          const payload = {
            ...data,
            ...(!isUserExist && { password }),
            recaptchaToken,
          };
          handleCreateAccountRequest(payload).then((resp) => {
            if (!String(resp.status).startsWith(responseTypes.SUCCESS)) {
              const errorId = resp?.data?.id;
              const errorTitleName = `error-titles.${errorId}`;
              const title = i18n.exists(errorTitleName)
                ? t(errorTitleName)
                : undefined;
              openErrorModal({
                text: i18n.exists(`errors.${errorId}`)
                  ? t(`errors.${errorId}`)
                  : t('common.purchase-error'),
                title,
              });
              return;
            }
          });
        });
      }
    };

    const formik: FormikProps<FormData> = useFormik<FormData>({
      initialValues,
      validationSchema: validationSchema(t, isUserExist),
      onSubmit,
    });
    const { errors, isValid } = formik;

    useEffect(() => {
      if (!createAccountResponse) return;
      const { data, status } = createAccountResponse;
      const {
        access_token,
        subscriptionType,
        subscriptionId,
        url,
        loginUrl,
        partner,
      } = data || {};
      if (String(status).startsWith(responseTypes.SUCCESS)) {
        setExistingUser(false);
        if (access_token) setToken(access_token);
        if (partner?.id) setPartnerId(partner?.id);
        if (subscriptionType === subscriptionTypes.PAID) {
          const redirectUrl = routesPaths.BUILD_PLAN.replace(
            ':hash',
            `${token}`
          );
          navigate(redirectUrl);
        } else {
          if (loginUrl) setLoginUrl(loginUrl);
          if (subscriptionId) setSubscriptionId(subscriptionId);
          if (url) {
            window.location.href = url;
          } else {
            const redirectUrl = routesPaths.CONFIRMED.replace(
              ':subscriptionId',
              subscriptionId as string
            );
            navigate(redirectUrl);
          }
        }
      }
    }, [isValid, createAccountResponse, navigate, token, setToken]);

    const SecondScreen = useMemo(() => {
      if (defaultMediaLink || !packageData) {
        const mediaLink = packageData?.createAccountMediaUrl || defaultMediaLink;

        return (
          <StyledVideoContainer>
            { packageStatus === apiStatuses.SUCCESS &&
              <StyledVideoScreen autoPlay loop muted poster={mediaLink}>
                <source src={mediaLink} type="video/mp4" />
              </StyledVideoScreen>
            }
          </StyledVideoContainer>
        );
      }

      return <StyledSideImage className="meetings-icon" />;
    }, [defaultMediaLink, packageData, packageStatus]);

    return (
      <>
        <Helmet>
          <title>{t('titles.create-account')}</title>
        </Helmet>

        <SplitScreenLayout secondScreen={SecondScreen}>
          <StyledContentContainer fitHeight>
            <StyledCreateAccountContainer>
              <StyledCreateAccountTitle as="span" className="main-header">
                {t('create-account.create-account')}
              </StyledCreateAccountTitle>
              <CreateAccountForm
                formik={formik}
                isLoading={createAccountLoading || isRecaptchaLoading}
                isUserExist={isUserExist}
                isFullForm={isFullForm}
                packageData={packageData as PackageDetail}
              />
            </StyledCreateAccountContainer>
          </StyledContentContainer>
        </SplitScreenLayout>
      </>
    );
  }
);
